import { useState, useRef, useEffect } from 'react';
import type React from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import Button from 'components/partials/button/button';
import Tooltip from 'components/partials/tooltip/tooltip';
import CopiedBadge from './copied-badge';
import CopyAnnouncer from './copy-announcer';

import { useMessageCopy } from '../../hooks/use-message-copy';

import {
  AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS,
  MESSAGE_ACTION_BUTTON_CLASS,
} from '../../ai-assistant.constants';

import Icons from 'assets/icons';

/** Minimum finger movement (px) before a touch is treated as scroll, not tap. */
const TOUCH_MOVE_THRESHOLD_PX = 10;

export interface UserMessageItemProps {
  /** User-authored message text. */
  content: string;
  /** When true, copy/edit interactions are disabled (e.g. read-only chat). */
  isDisabled?: boolean;
  /** Called when the user requests to edit this message. Omit to hide the edit action. */
  onEdit?: () => void;
}

/**
 * User message bubble with hover/tap/focus-revealed copy and edit actions.
 * Shows a fading "Copied" badge on success; action buttons stay mounted to
 * preserve focus.
 */
const UserMessageItem = ({
  content,
  isDisabled = false,
  onEdit,
}: UserMessageItemProps): React.JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchCopyVisible, setIsTouchCopyVisible] = useState(false);
  const [isCopyRevealSuppressed, setIsCopyRevealSuppressed] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchMovedRef = useRef(false);
  const {
    isShowingCopyConfirmation,
    copyConfirmationPhase,
    copyAnnouncement,
    copyContent,
  } = useMessageCopy(content);

  const resetTouchTracking = (): void => {
    touchStartRef.current = null;
    touchMovedRef.current = false;
  };

  const handleMouseEnter = (): void => {
    if (!isCopyRevealSuppressed && !isShowingCopyConfirmation) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = (): void => {
    setIsHovered(false);
    setIsTouchCopyVisible(false);
    setIsCopyRevealSuppressed(false);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!isShowingCopyConfirmation) {
      setIsCopyRevealSuppressed(false);
    }

    if (isDisabled || isShowingCopyConfirmation) {
      return;
    }

    const touch = event.touches[0];

    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchMovedRef.current = false;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!touchStartRef.current) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    if (deltaX > TOUCH_MOVE_THRESHOLD_PX || deltaY > TOUCH_MOVE_THRESHOLD_PX) {
      touchMovedRef.current = true;
    }
  };

  const handleTouchEnd = (): void => {
    if (!touchStartRef.current || isDisabled || isShowingCopyConfirmation) {
      resetTouchTracking();

      return;
    }

    if (!touchMovedRef.current) {
      setIsTouchCopyVisible(true);
    }

    resetTouchTracking();
  };

  const handleCopyClick = async (): Promise<void> => {
    const didCopy = await copyContent();

    if (didCopy) {
      setIsHovered(false);
      setIsTouchCopyVisible(false);
      setIsCopyRevealSuppressed(true);
    }
  };

  // Copy button stays mounted (opacity-only) so focus is not lost on copy.
  const isPointerRevealed = isHovered || isTouchCopyVisible;
  const isCopyButtonVisible =
    !isShowingCopyConfirmation && !isCopyRevealSuppressed && isPointerRevealed;

  const copyButtonStateClass = classNames(
    'transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100',
    isCopyButtonVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
  );

  useEffect(() => {
    if (!isTouchCopyVisible || isShowingCopyConfirmation) {
      return;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsTouchCopyVisible(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isShowingCopyConfirmation, isTouchCopyVisible]);

  return (
    <div
      ref={containerRef}
      data-testid="user-message-item"
      className={twMerge(
        'ml-auto flex w-fit flex-col text-md text-gray-900',
        AI_ASSISTANT_CHAT_MESSAGE_MAX_WIDTH_CLASS
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div className="rounded-md bg-gray-100 px-2.5 py-1.5">
        <p className="whitespace-pre-wrap break-words">{content}</p>
      </div>

      <div
        data-testid="message-user-copy-action"
        className="relative mt-1 flex h-8 justify-end"
      >
        <CopyAnnouncer message={copyAnnouncement} />
        {copyConfirmationPhase !== 'idle' && (
          <div className="absolute inset-0 flex items-center justify-end">
            <CopiedBadge phase={copyConfirmationPhase} />
          </div>
        )}
        <Tooltip content="Copy" position="bottom-end" enabled={!isDisabled}>
          <Button
            variant="reserve-text"
            className={classNames(
              MESSAGE_ACTION_BUTTON_CLASS,
              copyButtonStateClass
            )}
            onClick={() => {
              void handleCopyClick();
            }}
            disabled={isDisabled}
            aria-label="Copy"
            data-testid="message-action-copy-button"
          >
            <Icons.Copy />
          </Button>
        </Tooltip>
        {onEdit && (
          <Tooltip content="Edit" position="bottom-end" enabled={!isDisabled}>
            <Button
              variant="reserve-text"
              className={classNames(
                MESSAGE_ACTION_BUTTON_CLASS,
                copyButtonStateClass
              )}
              onClick={onEdit}
              disabled={isDisabled}
              aria-label="Edit"
              data-testid="message-action-edit-button"
            >
              <Icons.EditPencil />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default UserMessageItem;
